import { renderNav } from '../components/nav.js';
import { getLoadedKeys, type LoadedKey } from '../key-handoff.js';
import { isValidFrequency } from '../frequency.js';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function row(k: LoadedKey): string {
  const href = `#/frequency/${encodeURIComponent(k.frequency)}`;
  return `
    <a class="freq-row" href="${href}">
      <span class="freq-row-status" data-status></span>
      <span class="freq-row-addr">${escapeHtml(k.frequency)}</span>
      <span class="freq-row-label">${escapeHtml(k.label)}</span>
    </a>
  `;
}

const TUNER_HTML = `
  <form class="tuner" data-tuner novalidate>
    <input type="text" class="tuner-input" data-tuner-input
           placeholder="@____.____.____" maxlength="17" autocomplete="off"
           spellcheck="false" autocapitalize="off" aria-label="frequency address" />
    <button type="submit" class="btn btn-primary">tune in</button>
  </form>
  <p class="tuner-error" data-tuner-error hidden></p>
`;

function renderHtml(keys: LoadedKey[] | null): string {
  if (keys === null || keys.length === 0) {
    return `
      ${renderNav({ active: 'frequencies' })}
      <section class="empty-state">
        <h2>No keys loaded</h2>
        <p>Tune in to private frequencies by pasting a handoff link with <code>#keys=...</code> in the URL. Keys live in this tab's memory only and disappear when you close it.</p>
        <p>Raven users can use "View on Flock" to generate one. Without a key, you can browse the public firehose.</p>
        <p class="tuner-intro">Or tune to any frequency directly:</p>
        ${TUNER_HTML}
        <a class="btn btn-outline empty-state-firehose" href="#/firehose">browse the firehose</a>
      </section>
    `;
  }

  return `
    ${renderNav({ active: 'frequencies' })}
    <section class="page freq-page">
      <header class="freq-header">
        <h1>Your frequencies</h1>
        <p class="freq-sub">${keys.length} key${keys.length === 1 ? '' : 's'} loaded · keys live only in this tab</p>
      </header>
      <div class="freq-list">
        ${keys.map(row).join('')}
      </div>
      <div class="tuner-section">
        <p class="tuner-intro">Tune to any frequency:</p>
        ${TUNER_HTML}
      </div>
    </section>
  `;
}

function wireTuner(root: HTMLElement): void {
  const form = root.querySelector<HTMLFormElement>('[data-tuner]');
  const input = root.querySelector<HTMLInputElement>('[data-tuner-input]');
  const error = root.querySelector<HTMLElement>('[data-tuner-error]');
  if (form === null || input === null || error === null) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = input.value.trim().toLowerCase();
    if (!isValidFrequency(value)) {
      input.classList.add('invalid');
      error.textContent = 'Format: @xxxx.xxxx.xxxx (lowercase letters and digits, three groups of four)';
      error.hidden = false;
      return;
    }
    input.classList.remove('invalid');
    error.hidden = true;
    window.location.hash = `#/frequency/${encodeURIComponent(value)}`;
  });

  input.addEventListener('input', () => {
    if (input.classList.contains('invalid')) {
      input.classList.remove('invalid');
      error.hidden = true;
    }
  });
}

export function mountFrequencies(root: HTMLElement): () => void {
  root.innerHTML = renderHtml(getLoadedKeys());
  wireTuner(root);
  return () => undefined;
}
